/* eslint-disable react/no-unescaped-entities */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import InputRange, { Range } from 'react-input-range';

import Select2, { DataSelect2 } from 'wdna-select2';

import { UserDO } from 'wdna-components/lib/types.new';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import '../../../../../assets/css/input-range.css';

import { ParameterDO, TypeSensorVariableDO, VariableDO, LovDO } from '../../../configuration/requests';
import { SensorDO } from '../../../sensors/requests';
import { AlarmDO } from '../../requests';

/**
 * Function to format the label of the alarm
 *
 * @param value to format
 * @returns the formatted label
 */
function formatLabel(value: number): string {
  return value
    .toFixed(2)
    .toString()
    .replace('.', ':')
    .replace(':5', ':3')
    .replace('24', '0');
}

/**
 *
 */
function validCharacters(): void {
  // eslint-disable-next-line no-control-regex
  const regexp: RegExp = /[\x00-\x7F]/g;
  const x: HTMLElement | null = document.getElementById('smsBody' || 'voiceBody');
  const array: RegExpMatchArray[] = [...(x as HTMLInputElement).value.matchAll(regexp)];
  (x as HTMLInputElement).value = array.join('');
}

type CustomAlarmDO = Partial<AlarmDO> & {
  checkboxMin?: boolean;
  checkboxMax?: boolean;
}

interface Props {
  formValues: {
    loading?: boolean;

    sensorArray: SensorDO[];
    typeVariableArray: TypeSensorVariableDO[];
    variableArray: VariableDO[];
    actionArray: LovDO[];
    timeZoneArray: LovDO[];

    typeAlarm: boolean;
    previewText?: string;

    defEmailSubject?: ParameterDO;
    defNcxSubject?: ParameterDO;
    defSmsBody?: ParameterDO;
    defNcxBody?: ParameterDO;
    defVoiceBody?: ParameterDO;
    defEmailBody?: ParameterDO;

    timetableL1: Range;
    timetableM1: Range;
    timetableX1: Range;
    timetableJ1: Range;
    timetableV1: Range;
    timetableS1: Range;
    timetableD1: Range;
    timetableL2: Range;
    timetableM2: Range;
    timetableX2: Range;
    timetableJ2: Range;
    timetableV2: Range;
    timetableS2: Range;
    timetableD2: Range;

    data: CustomAlarmDO;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onChangeAction: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeCheckbox: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDevice: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeRange: (l: string, v: Range | number) => void;
  onClickRadio: (e: React.MouseEvent<HTMLInputElement>) => void;
  onClickPreview: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onResetRange: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onResetRangeTwo: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onMaxRange: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onMaxRangeTwo: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Function to generate a form component
 *
 * @param props properties of the component
 * @returns jsx element with the form
 */
function Form(props: Props): JSX.Element {
  const { sensorArray, typeVariableArray, actionArray, timeZoneArray } = props.formValues;
  const { timetableL1, timetableM1, timetableX1, timetableJ1, timetableV1, timetableS1, timetableD1 } = props.formValues;
  const { timetableL2, timetableM2, timetableX2, timetableJ2, timetableV2, timetableS2, timetableD2 } = props.formValues;
  const { defEmailSubject, defEmailBody, defSmsBody, defVoiceBody, defNcxSubject, defNcxBody } = props.formValues;

  const { data, previewText, typeAlarm } = props.formValues;

  const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
  const userIsURW: boolean = hasLevel('role', ['ADM', 'URW']);

  let isEmail: boolean = false;
  let isSms: boolean = false;
  let isVoice: boolean = false;
  let isHardware: boolean = false;

  let messageMaxLength: number = 160;
  let messageBodyName: string = '';
  let messageSubjectDef: string = (defNcxSubject ? defNcxSubject.value : '');
  let messageBodyDef: string = (defNcxBody ? defNcxBody.value : '');

  switch (data.action?.toUpperCase()) {
    case 'SMS':
      isSms = true;
      messageBodyName = 'smsBody';
      messageMaxLength = 160;
      if (typeAlarm) messageBodyDef = _.defaultTo(defSmsBody?.value, '');
      break;
    case 'VOICE':
      isVoice = true;
      messageBodyName = 'voiceBody';
      messageMaxLength = 160;
      if (typeAlarm) messageBodyDef = _.defaultTo(defVoiceBody?.value, '');
      break;
    case 'EMAIL':
      isEmail = true;
      messageBodyName = 'mailBody';
      messageMaxLength = 450;
      if (typeAlarm) messageSubjectDef = _.defaultTo(defEmailSubject?.value, '');
      if (typeAlarm) messageBodyDef = _.defaultTo(defEmailBody?.value, '');
      break;
    default:
      isHardware = true;
      messageBodyName = 'body';
  }

  return (
    <div>
      <fieldset>
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Device</b>&nbsp;&nbsp;&nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.sensorId"
                name="data.sensorId"
                value={_.defaultTo(data?.sensorId, '')}
                onChange={props.onChangeDevice}
                data-ctype="number"
                data={sensorArray
                  .filter((sens: SensorDO): boolean => {
                    return userIsURW || String(sens.companyId) === user?.access?.companyId;
                  })
                  .map((item: SensorDO): DataSelect2 => {
                    return {
                      id: item.id,
                      text: `${item.companyName} - ${item.code}: ${item.name}`,
                    };
                  })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
          <div className="col-3">
            <label className="label">
              <b>Status</b>&nbsp;&nbsp;&nbsp;
            </label>
            <input
              type="checkbox"
              id="data.active"
              name="data.active"
              checked={_.defaultTo(data.active, false)}
              onChange={props.onChangeCheckbox}
              data-ctype="boolean"
              className="input-new"
            />
            &nbsp;&nbsp;
            <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              Active
            </label>
          </div>
          <div className="col-3" id="alarmType">
            <label className="label">
              <b>Type</b>&nbsp;&nbsp;&nbsp;
            </label>
            <input
              id="typeAlarmVAR"
              name="typeAlarm"
              type="radio"
              value="1"
              checked={_.defaultTo(typeAlarm, false)}
              onChange={(): void => { /* pass */ }}
              onClick={props.onClickRadio}
              data-ctype="number"
              className="input-new"
            />
            &nbsp; &nbsp;
            <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              Variable
            </label>
            &emsp;
            <input
              id="typeAlarmDIS"
              name="typeAlarm"
              type="radio"
              value="0"
              checked={_.defaultTo(!typeAlarm, false)}
              onChange={(): void => { /* pass */ }}
              onClick={props.onClickRadio}
              data-ctype="number"
              className="input-new"
            />
            &nbsp; &nbsp;
            <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              Disconnection
            </label>
          </div>
        </div>
        <br />
        <div hidden={!typeAlarm}>
          <div className="row">
            <div className="col-6">
              <label className="label">
                <b>Variable</b>&nbsp;
              </label>
              <span className="mandatory">
                <Select2
                  id="data.typeSensorVariableId"
                  name="data.typeSensorVariableId"
                  value={_.defaultTo(data?.typeSensorVariableId, '')}
                  onChange={props.onChange}
                  data-ctype="number"
                  data={typeVariableArray.map((item: TypeSensorVariableDO): DataSelect2 => {
                    return { text: `${item.order}: ${item.variableName}`, id: item.id };
                  })}
                  options={{ placeholder: 'Select option' }}
                  style={{ width: '80%' }}
                  required
                />
              </span>
            </div>
            <div className="col-6">
              <label className="label">
                <b>Minutes to exceed before warning</b>&nbsp; &nbsp;&nbsp;
              </label>
              <input
                type="number"
                id="data.minutes"
                name="data.minutes"
                value={_.defaultTo(data.minutes, '')}
                onChange={props.onChange}
                data-ctype="number"
                className="input-new"
              />
              &emsp;&emsp;
              <input
                type="checkbox"
                id="data.accumulate"
                name="data.accumulate"
                checked={_.defaultTo(data.accumulate, false)}
                onChange={props.onChangeCheckbox}
                data-ctype="boolean"
                className="input-new"
              />
              &nbsp;&nbsp;
              <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                Cumulative
              </label>
            </div>
          </div>
          <br />
          <div className="row">
            <div className="col-2">
              <input
                type="checkbox"
                id="data.checkboxMin"
                name="data.checkboxMin"
                checked={_.defaultTo(data.checkboxMin, false)}
                onChange={props.onChangeCheckbox}
                className="input-new"
              />
              &nbsp; &nbsp;
              <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                Minimum
              </label>
              <br />
              &nbsp; &nbsp; &nbsp; &#8239; &#8239;
              <input
                type="number"
                id="data.min"
                name="data.min"
                value={_.defaultTo(data.min, '')}
                onChange={props.onChange}
                data-ctype="number"
                className="input-new"
                disabled={!data.checkboxMin}
              />
            </div>
            <div className="col-2">
              <input
                type="checkbox"
                id="data.checkboxMax"
                name="data.checkboxMax"
                checked={_.defaultTo(data.checkboxMax, false)}
                onChange={props.onChangeCheckbox}
                className="input-new"
              />
              &nbsp; &nbsp;
              <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                Maximum
              </label>
              <br />
              &nbsp; &nbsp; &nbsp; &nbsp;
              <input
                type="number"
                id="data.max"
                name="data.max"
                value={_.defaultTo(data.max, '')}
                onChange={props.onChange}
                data-ctype="number"
                className="input-new"
                disabled={!data.checkboxMax}
              />
            </div>
            <div className="col-2">
              <label className="label">
                <b>Repeatability time (minutes)</b>&nbsp; &nbsp;&nbsp;
              </label>
              <input
                type="number"
                id="data.repeat"
                name="data.repeat"
                value={_.defaultTo(data.repeat, '')}
                onChange={props.onChange}
                data-ctype="number"
                className="input-new"
              />
            </div>
          </div>
        </div>
        <div hidden={typeAlarm}>
          <div className="row" id="divNc">
            <div className="col-6">
              <label className="label">
                <b>Minutes disconnected before warning</b> &nbsp; &nbsp;&nbsp;
              </label>
              <input
                type="number"
                id="data.disconnection"
                name="data.disconnection"
                value={_.defaultTo(data.disconnection, '')}
                onChange={props.onChange}
                data-ctype="number"
                className="input-new"
              />
            </div>
          </div>
        </div>
        <br />
        <br />
        <div className="row">
          <div className="col-6">
            <h2>
              <span className="widget-icon" style={{ padding: '4px' }}>
                <i className="fa fa-edit" />
              </span>
              Warning &nbsp;&nbsp;
            </h2>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Action</b>&nbsp; &nbsp;&nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.action"
                name="data.action"
                value={_.defaultTo(data.action, '')}
                onChange={props.onChangeAction}
                data={actionArray.map((item: LovDO): DataSelect2 => {
                  return { id: item.id, text: item.name };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
          <div className="col-6" hidden={!isHardware}>
            <label className="label">
              <b>Gateway</b>&nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.gatewayId"
                name="data.gatewayId"
                value={_.defaultTo(data?.gatewayId, '')}
                onChange={props.onChange}
                data-ctype="number"
                data={sensorArray.map((item: SensorDO): DataSelect2 => {
                  return { text: `${item.companyId} - ${item.name} (${item.code})`, id: item.id };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
        </div>
        <br />
        <div hidden={!isHardware}>
          <div className="row">
            <div className="col-6">
              <label className="label">
                <b>Actuation time</b>&nbsp;
              </label>
              <input
                type="number"
                id="data.launch"
                name="data.launch"
                value={_.defaultTo(data.launch, '')}
                onChange={props.onChange}
                data-ctype="number"
                className="input-new"
              />
            </div>
          </div>
          <br />
        </div>
        <div hidden={!isSms}>
          <div className="row">
            <div className="col-6">
              <label className="label">
                <b>Phone number to receive SMS (separate the different options by ';' max:9)</b>&nbsp; &nbsp;&nbsp;
              </label>
              <input
                type="text"
                id="data.smsTo"
                name="data.smsTo"
                value={_.defaultTo(data.smsTo, '')}
                onChange={props.onChange}
                className="input-mandatory"
                placeholder="Phone number to receive SMS"
                required
              />
              &emsp;&emsp;
              <input
                type="checkbox"
                id="data.msgUnique1"
                name="data.msgUnique"
                checked={_.defaultTo(data.msgUnique, false)}
                onChange={props.onChangeCheckbox}
                data-ctype="boolean"
                className="input-new"
              />
              &nbsp;&nbsp;
              <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                Send only once
              </label>
            </div>
          </div>
          <br />
        </div>
        <div hidden={!isVoice}>
          <div className="row">
            <div className="col-6">
              <label className="label">
                <b>Phone number to receive calls (separate the different options by ';' max:9)</b>&nbsp; &nbsp;&nbsp;
              </label>
              <input
                type="text"
                id="data.voiceTo"
                name="data.voiceTo"
                value={_.defaultTo(data.voiceTo, '')}
                onChange={props.onChange}
                className="input-mandatory"
                placeholder="Phone number to receive calls"
                required
              />
              &emsp;&emsp;
              <input
                type="checkbox"
                id="data.msgUnique2"
                name="data.msgUnique"
                checked={_.defaultTo(data.msgUnique, false)}
                onChange={props.onChangeCheckbox}
                data-ctype="boolean"
                className="input-new"
              />
              &nbsp;&nbsp;
              <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                Send only once
              </label>
            </div>
          </div>
          <br />
        </div>
        <div hidden={!isEmail}>
          <div className="row">
            <div className="col-6">
              <label className="label">
                <b>Recipient (separate the different options by ';' max:8)</b>&nbsp;
              </label>
              <input
                type="text"
                id="data.mailTo"
                name="data.mailTo"
                value={_.defaultTo(data.mailTo, '')}
                onChange={props.onChange}
                className="input-mandatory"
                style={{ width: '40%' }}
                placeholder="Recipient"
                size={40}
                required
              />
              &emsp;&emsp;
              <input
                type="checkbox"
                id="data.msgUnique3"
                name="data.msgUnique"
                checked={_.defaultTo(data.msgUnique, false)}
                onChange={props.onChangeCheckbox}
                data-ctype="boolean"
                className="input-new"
              />
              &nbsp;&nbsp;
              <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                Send only once
              </label>
            </div>
            <div className="col-6">
              <label className="label">
                <b>Subject</b> &nbsp; &nbsp;&nbsp;
              </label>
              <input
                type="text"
                id="data.mailSubject"
                name="data.mailSubject"
                value={_.defaultTo(data.mailSubject, messageSubjectDef)}
                onChange={props.onChange}
                className="input-new"
                style={{ width: '80%' }}
                placeholder="Subject"
                size={40}
              />
            </div>
          </div>
        </div>
        <br />
        <div hidden={isHardware}>
          <div className="row">
            <div className="col-6">
              <label className="label">
                <b>Body</b>
                <br />
                Available characters:{' '}
                {messageMaxLength - ((data[messageBodyName] && data[messageBodyName].length) || messageBodyDef.length)}
                <br />
                Available variables: {'{sensor}, {name}, {description}'}
                {typeAlarm && ', {variable}, {min}, {max}, {unit}, {reading}'}
                &nbsp; &nbsp; &nbsp; &nbsp;&nbsp;
              </label>
              <textarea
                id={`data.${messageBodyName}`}
                name={`data.${messageBodyName}`}
                value={_.defaultTo(data[messageBodyName], messageBodyDef)}
                onChange={props.onChange}
                onKeyUp={validCharacters}
                className="input-new"
                style={{ width: '80%' }}
                maxLength={messageMaxLength}
                rows={5}
                cols={55}
              />
            </div>
            <div className="col-6">
              <button
                type="button"
                onClick={props.onClickPreview}
                className="btn btn-default"
                style={{ margin: '10px 0 0 5px', padding: '0 22px', height: '31px' }}
              >
                Preview text
              </button>
              <br /><br />
              <textarea
                value={_.defaultTo(previewText, '')}
                className="input-new"
                style={{ width: '80%' }}
                readOnly
                rows={5}
                cols={55}
                maxLength={160}
              />
            </div>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>TimeZone</b>&nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.timezone"
                name="data.timezone"
                value={_.defaultTo(data?.timezone, '')}
                onChange={props.onChange}
                data={timeZoneArray.map((item: LovDO): DataSelect2 => {
                  return { text: item.name, id: item.id };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
          <div className="col-6" />
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <h2>
              <span className="widget-icon" style={{ padding: '4px' }}>
                <i className="fa fa-edit" />
              </span>
              Scheduling &nbsp;&nbsp;
            </h2>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <button type="button" className="btn btn-default" onClick={(): void => props.onResetRange()}>
                &nbsp;&nbsp; RESET ALL &nbsp;&nbsp;
            </button>
            &nbsp;&nbsp;
            <button type="button" className="btn btn-default" onClick={(): void => props.onMaxRange()}>
                &nbsp;&nbsp; SET ALL &nbsp;&nbsp;
            </button>
          </div>
          <div className="col-6">
            <button type="button" className="btn btn-default" onClick={(): void => props.onResetRangeTwo()}>
                &nbsp;&nbsp; RESET ALL &nbsp;&nbsp;
            </button>
            &nbsp;&nbsp;
            <button type="button" className="btn btn-default" onClick={(): void => props.onMaxRangeTwo()}>
                &nbsp;&nbsp; SET ALL &nbsp;&nbsp;
            </button>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Monday: {formatLabel(timetableL1.min)} - {formatLabel(timetableL1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableL1"
              value={timetableL1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableL1', value)}
            />
          </div>
          <div className="col-6" style={{ maxWidth: '55%' }}>
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Monday: {formatLabel(timetableL2.min)} - {formatLabel(timetableL2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableL2"
              value={timetableL2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableL2', value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Tuesday: {formatLabel(timetableM1.min)} - {formatLabel(timetableM1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableM1"
              value={timetableM1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableM1', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Tuesday: {formatLabel(timetableM2.min)} -{' '}
              {formatLabel(timetableM2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableM2"
              value={timetableM2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableM2', value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Wednesday: {formatLabel(timetableX1.min)} - {formatLabel(timetableX1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableX1"
              value={timetableX1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableX1', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Wednesday: {formatLabel(timetableX2.min)} - {formatLabel(timetableX2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableX2"
              value={timetableX2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableX2', value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Thursday: {formatLabel(timetableJ1.min)} - {formatLabel(timetableJ1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableJ1"
              value={timetableJ1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableJ1', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Thursday: {formatLabel(timetableJ2.min)} - {formatLabel(timetableJ2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableJ2"
              value={timetableJ2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableJ2', value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Friday: {formatLabel(timetableV1.min)} - {formatLabel(timetableV1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableV1"
              value={timetableV1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableV1', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Friday: {formatLabel(timetableV2.min)} - {formatLabel(timetableV2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableV2"
              value={timetableV2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableV2', value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Saturday: {formatLabel(timetableS1.min)} - {formatLabel(timetableS1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableS1"
              value={timetableS1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableS1', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Saturday: {formatLabel(timetableS2.min)} - {formatLabel(timetableS2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableS2"
              value={timetableS2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableS2', value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Sunday: {formatLabel(timetableD1.min)} - {formatLabel(timetableD1.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableD1"
              value={timetableD1}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableD1', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              &nbsp;&nbsp;&nbsp;&nbsp;Sunday: {formatLabel(timetableD2.min)} - {formatLabel(timetableD2.max)}
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetableD2"
              value={timetableD2}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetableD2', value)}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
}

export default Form;
